import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Layers, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { workspaceAdminAPI } from '../services/api';
import { useAuth, PROXY_CANCELLED } from '../contexts/AuthContext';
import { getErrorMessage } from '../lib/utils';
import { getHomeRouteByRole } from '../lib/rbac';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const SubTenantsCombinedDashboard = () => {
  const navigate = useNavigate();
  const { proxyLogin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [switchingSlug, setSwitchingSlug] = useState('');
  const [overview, setOverview] = useState({ tenant: null, totals: {}, items: [] });
  const [proxyTargets, setProxyTargets] = useState({ items: [], source_tenant_id: null, source_tenant_slug: null });

  const items = useMemo(() => {
    const overviewItems = Array.isArray(overview?.items) ? overview.items : [];
    const targetItems = Array.isArray(proxyTargets?.items) ? proxyTargets.items : [];
    const sourceTenantId = Number(proxyTargets?.source_tenant_id || overview?.tenant?.id || 0);
    const fallbackChildren = targetItems.filter((row) => Number(row.parent_tenant_id) === sourceTenantId);
    const merged = new Map();

    overviewItems.forEach((row) => {
      const key = String(row?.slug || row?.id || '');
      if (key) merged.set(key, row);
    });
    fallbackChildren.forEach((row) => {
      const key = String(row?.slug || row?.id || '');
      if (!key) return;
      merged.set(key, {
        user_count: 0,
        active_user_count: 0,
        employee_count: 0,
        ...row,
        ...(merged.get(key) || {}),
      });
    });

    return Array.from(merged.values());
  }, [overview?.items, overview?.tenant?.id, proxyTargets?.items, proxyTargets?.source_tenant_id]);

  const totals = useMemo(() => {
    const overviewTotals = overview?.totals && typeof overview.totals === 'object' ? overview.totals : {};
    if (Number(overviewTotals?.subtenant_count || 0) > 0) return overviewTotals;
    return {
      subtenant_count: items.length,
      total_users: items.reduce((sum, row) => sum + Number(row?.user_count || 0), 0),
      total_employees: items.reduce((sum, row) => sum + Number(row?.employee_count || 0), 0),
    };
  }, [items, overview?.totals]);

  const parentTenantLabel = useMemo(() => {
    return (
      overview?.tenant?.name ||
      overview?.tenant?.slug ||
      proxyTargets?.source_tenant_slug ||
      'Parent tenant overview'
    );
  }, [overview?.tenant?.name, overview?.tenant?.slug, proxyTargets?.source_tenant_slug]);

  const loadOverview = useCallback(async () => {
    try {
      setLoading(true);
      const [overviewResult, proxyTargetsResult] = await Promise.allSettled([
        workspaceAdminAPI.getChildTenantsOverview(),
        workspaceAdminAPI.listProxyTargets(),
      ]);

      if (overviewResult.status === 'fulfilled') {
        setOverview(overviewResult.value?.data || { tenant: null, totals: {}, items: [] });
      } else {
        setOverview({ tenant: null, totals: {}, items: [] });
      }

      if (proxyTargetsResult.status === 'fulfilled') {
        setProxyTargets(proxyTargetsResult.value?.data || { items: [], source_tenant_id: null, source_tenant_slug: null });
      } else {
        setProxyTargets({ items: [], source_tenant_id: null, source_tenant_slug: null });
      }

      if (overviewResult.status !== 'fulfilled' && proxyTargetsResult.status !== 'fulfilled') {
        throw overviewResult.reason || proxyTargetsResult.reason || new Error('Failed to load sub-tenant data');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load sub-tenant overview'));
      setOverview({ tenant: null, totals: {}, items: [] });
      setProxyTargets({ items: [], source_tenant_id: null, source_tenant_slug: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const handleSwitch = useCallback(async (slug) => {
    const nextSlug = String(slug || '').trim();
    if (!nextSlug) return;
    try {
      setSwitchingSlug(nextSlug);
      const response = await proxyLogin(nextSlug);
      navigate(getHomeRouteByRole(response?.role));
      toast.success('Tenant view updated');
    } catch (error) {
      if (error?.code === PROXY_CANCELLED) return;
      toast.error(getErrorMessage(error, 'Failed to switch tenant view'));
    } finally {
      setSwitchingSlug('');
    }
  }, [navigate, proxyLogin]);

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading sub-tenants...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="border-primary/15 shadow-sm">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-primary" />
                Combined Sub-Tenant Dashboard
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {parentTenantLabel === 'Parent tenant overview' ? parentTenantLabel : `Parent tenant: ${parentTenantLabel}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={loadOverview} disabled={Boolean(switchingSlug)}>
                Refresh
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin')} disabled={Boolean(switchingSlug)}>
                Back
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <p className="text-sm font-medium">Sub-Tenants</p>
            </div>
            <p className="mt-2 text-2xl font-semibold">{Number(totals?.subtenant_count || items.length || 0)}</p>
          </div>
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <p className="text-sm font-medium">Users</p>
            </div>
            <p className="mt-2 text-2xl font-semibold">{Number(totals?.total_users || 0)}</p>
          </div>
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <p className="text-sm font-medium">Employees</p>
            </div>
            <p className="mt-2 text-2xl font-semibold">{Number(totals?.total_employees || 0)}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-primary/10 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Sub-Tenants</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground">No sub-tenants available.</div>
          ) : (
            <div className="space-y-2">
              {items.map((row) => (
                <div key={row.id || row.slug} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium truncate">{row.name || row.slug}</p>
                      <Badge variant="outline">{row.slug}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Users: {Number(row.user_count || 0)} • Employees: {Number(row.employee_count || 0)}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleSwitch(row.slug)}
                    disabled={Boolean(switchingSlug) && switchingSlug !== String(row.slug || '')}
                  >
                    {switchingSlug === String(row.slug || '') ? 'Switching...' : 'Switch'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubTenantsCombinedDashboard;
